import React, { useRef } from 'react'
import { Input } from '@/components/ui/input'

interface OtpInputProps {
  value: string[]
  onChange: (newOtp: string[]) => void
  onClearError?: () => void
}

export function OtpInput({ value, onChange, onClearError }: OtpInputProps) {
  const otpRefs = useRef<(HTMLInputElement | null)[]>([])

  const handleOtpChange = (index: number, val: string) => {
    const numericValue = val.replace(/\D/g, '')

    if (numericValue.length > 1) {
      const chars = numericValue.slice(0, 6 - index).split('')
      const newOtp = [...value]
      chars.forEach((char, idx) => {
        if (index + idx < 6) newOtp[index + idx] = char
      })
      onChange(newOtp)

      const nextIdx = Math.min(index + chars.length, 5)
      otpRefs.current[nextIdx]?.focus()
    } else {
      const newOtp = [...value]
      newOtp[index] = numericValue
      onChange(newOtp)

      if (numericValue && index < 5) {
        otpRefs.current[index + 1]?.focus()
      }
    }

    onClearError?.()
  }

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedData = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, 6)
    if (!pastedData) return

    const newOtp = [...value]
    for (let i = 0; i < pastedData.length; i++) {
      newOtp[i] = pastedData[i]
    }
    onChange(newOtp)

    const focusIndex = Math.min(pastedData.length, 5)
    otpRefs.current[focusIndex]?.focus()

    onClearError?.()
  }

  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      otpRefs.current[index - 1]?.focus()
    }
  }

  return (
    <div className="flex justify-between sm:justify-center gap-1.5 sm:gap-2.5">
      {value.map((digit, idx) => (
        <Input
          key={idx}
          ref={(e: HTMLInputElement | null) => {
            otpRefs.current[idx] = e
          }}
          type="text"
          size="large"
          inputMode="numeric"
          value={digit}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleOtpChange(idx, e.target.value)
          }
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
            handleOtpKeyDown(idx, e)
          }
          onPaste={handleOtpPaste}
          maxLength={1}
          autoComplete="one-time-code"
        />
      ))}
    </div>
  )
}
